import { makeAutoObservable } from "mobx";
import { Price } from "../Price";
import { Availability } from "../Availability";
import { Quantity } from "../Quantity";
import { ModifierItemInstanceId } from "./ModifierItemInstanceId";
import { randomUUID } from "../../id";
import { ParameterScope } from "../ParameterScope";
export class ModifierItemInstance {
    constructor(params) {
        this.scope = ParameterScope.Uninitialized;
        this.itemQuantities = params.itemQuantities;
        this.instanceId = params.instanceId;
        this.modifierItemType = params.modifierItemType;
        this.maxDuplicatesPerItem = params.maxDuplicatesPerItem;
        this.defaultQuantity = params.defaultQuantity;
        this.isMultipleChoice = params.isMultipleChoice;
        this.editability = params.editability;
        makeAutoObservable(this);
    }
    setScope(scope) {
        this.scope = scope;
        this.editability.setScope(scope);
    }
    get typeId() {
        return this.modifierItemType.id;
    }
    get quantity() {
        return this.itemQuantities.quantity(this.typeId);
    }
    get isEditable() {
        return this.editability.isPossiblyEditable;
    }
    get unitPrice() {
        return Price.scoped(this.modifierItemType.pricing, this.scope);
    }
    get defaultPrice() {
        return this.unitPrice.multiply(this.defaultQuantity);
    }
    get unitAvailability() {
        return Availability.ofModifierItemType(this.modifierItemType, this.scope);
    }
    get isDefinitelyAvailable() {
        return this.unitAvailability.isAvailable.isDefinitelyTrue;
    }
    get isDefinitelyUnavailable() {
        return this.unitAvailability.isAvailable.isDefinitelyFalse;
    }
    get isDefault() {
        return this.defaultQuantity.value > 0;
    }
    get isFinal() {
        return this.isDefault || !this.isDefinitelyUnavailable;
    }
    get canIncrement() {
        if (!this.isDefinitelyAvailable || !this.isEditable) {
            return false;
        }
        if (this.maxDuplicatesPerItem !== null &&
            this.quantity.value >= this.maxDuplicatesPerItem) {
            return false;
        }
        return true;
    }
    increment() {
        if (this.canIncrement) {
            this.itemQuantities.increment(this.typeId);
        }
    }
    get canDecrement() {
        var _a;
        if (!this.isDefinitelyAvailable || !this.isEditable) {
            return false;
        }
        if (this.modifierItemType.irremovable) {
            const defaultItemQuantity = (_a = this.defaultQuantity) !== null && _a !== void 0 ? _a : 0;
            if (this.quantity.value <= defaultItemQuantity.value) {
                return false;
            }
        }
        return this.quantity.value > 0;
    }
    decrement() {
        if (this.canDecrement) {
            this.itemQuantities.decrement(this.typeId);
        }
    }
    get isSelected() {
        return this.quantity.value > 0;
    }
    setSelected(selected) {
        if (!this.isEditable) {
            throw new Error(`ModifierItemInstance not editable`);
        }
        if (this.modifierItemType.irremovable && this.isDefault && !selected) {
            return;
        }
        this.itemQuantities.setQuantity(this.typeId, selected ? Quantity.One : Quantity.Zero);
    }
    select() {
        if (!this.isEditable) {
            throw new Error(`ModifierItemInstance not editable`);
        }
        this.itemQuantities.clear();
        this.itemQuantities.setQuantity(this.typeId, Quantity.One);
    }
    get isDisabled() {
        if (this.isDefinitelyUnavailable) {
            const irremovableIngredient = this.isDefault && this.modifierItemType.irremovable;
            const temporarilyUnavailable = this.unitAvailability.unavailabilityReasons.includes("TemporarilyUnavailable");
            return irremovableIngredient || temporarilyUnavailable;
        }
        return false;
    }
    /** See {@link ProductInstanceCreator.fixAvailability} */
    fixAvailability() {
        if (this.isDefinitelyUnavailable) {
            this.itemQuantities.delete(this.typeId);
        }
    }
    clone(itemQuantities) {
        return new ModifierItemInstance({
            itemQuantities,
            instanceId: new ModifierItemInstanceId(randomUUID()),
            modifierItemType: this.modifierItemType,
            maxDuplicatesPerItem: this.maxDuplicatesPerItem,
            defaultQuantity: this.defaultQuantity,
            isMultipleChoice: this.isMultipleChoice,
            editability: this.editability,
        });
    }
}
